import type {
  ActorRef,
  EXTENSION_MANIFEST_API_VERSION as THIRD_PARTY_EXTENSION_RUNTIME_API_VERSION,
  FileViewerDeclaration,
  HostMethodCalls,
  HostMethodName,
  ProviderHookName,
  ToolCardDeclaration,
} from '@xuwo/contracts';
import { PROVIDER_HOOK_NAMES } from '@xuwo/contracts';

export const EXTENSION_WORKSPACE_ID_MAX_LENGTH = 256;

export function requireExtensionWorkspaceId(value: unknown): string {
  if (
    typeof value !== 'string' ||
    value.length < 1 ||
    value.length > EXTENSION_WORKSPACE_ID_MAX_LENGTH ||
    /[\u0000-\u001f\u007f]/u.test(value) ||
    value.trim() !== value
  ) {
    throw new Error('Invalid extension workspace identity.');
  }
  return value;
}

/** `host.browser.open` 的参数(D586 第 8 期)。 */
export type ExtensionBrowserOpenInput = Readonly<{
  url: string;
  /** 允许的 http(s) origin;导航、脚本、响应观察与 Cookie 都不出这个范围。 */
  origins: readonly string[];
  title?: string;
  /** 默认 `panel`(侧栏浏览器标签页,面板不应答时退回窗口)。 */
  surface?: 'panel' | 'window' | 'hidden';
  /** 只观察这些 URL 前缀开头、且在白名单内的响应。 */
  watch?: readonly string[];
}>;

/**
 * 包声明的一个实体类型(`entityTypes[]`,16b),带目录里的类型 id 与它回答的引用
 * 种类(见合同 `extensionEntityTypes`)。
 */
export type ExtensionEntityTypeContributionDto =
  import('@xuwo/contracts').ExtensionEntityType &
    Readonly<{ packageId: string; packageVersion: string }>;

/** 插件经历来源回给宿主的一行原始事件(合同 `activitySourceRowSchema`)。 */
export type ExtensionActivitySourceRow =
  import('@xuwo/contracts').ActivitySourceRow;

/** 已启用包声明的经历来源(`activitySources[]`)。 */
export type ExtensionActivitySourceContributionDto = Readonly<{
  packageId: string;
  /** 本包的标题:来源没写 `title` 时经历页按它显示。 */
  packageTitle: Readonly<{ zh: string; en: string }>;
  declaration: ThirdPartyActivitySourceDeclaration;
}>;

/** `bundle.widgets[]` 在统一扩展目录中的只读投影。 */
export type ExtensionWidgetViewContributionDto = Readonly<{
  type: 'widget.view';
  id: string;
  packageId: string;
  packageVersion: string;
  enabled: boolean;
  declaration: ThirdPartyBundleWidgetDeclaration;
  /** 清单 `fileViewers[]` 里指向这个小组件的查看器声明(D651)。 */
  fileViewers?: readonly FileViewerDeclaration[];
  /** 清单 `toolCards[]` 里用这个小组件画的工具卡(D798)。 */
  toolCards?: readonly ToolCardDeclaration[];
}>;

export type ExtensionBundleContentDto = Readonly<{
  packageId: string;
  packageVersion: string;
  widgets: readonly ThirdPartyBundleWidgetDeclaration[];
  /**
   * 文档模板(新建时复制):`documentTemplates[]` 与旧写法 `bundle.templates[]` 读成
   * 同一形状(`extensionDocumentTemplates`)。
   */
  templates: readonly (import('@xuwo/contracts').ExtensionDocumentTemplate &
    Readonly<{ markdown: string }>)[];
  /** 功能页、实体样式与插件主页的正文,按清单里的路径(`pages[].body` 等)。 */
  bodies: Readonly<Record<string, string>>;
}>;

export type PackageWidgetModuleDto = Readonly<{
  packageId: string;
  packageVersion: string;
  registrationGeneration: number;
  widget: ThirdPartyBundleWidgetDeclaration;
  /** 这个小组件能按 MIME 打开的文件(清单 `fileViewers[]`,D651)。 */
  fileViewers?: readonly FileViewerDeclaration[];
  /** 这个小组件画的工具卡(清单 `toolCards[]`,D798)。 */
  toolCards?: readonly ToolCardDeclaration[];
  /**
   * 组件入口模块的地址(D717 A):包文件通道 `assets://./extension-package/<包>/<版本>/
   * module/<工作区令牌>/<激活段>/<入口路径>`,窗口直接 `import()`,共享块与按需块按相对
   * 路径跟着走。界面暂停或文件读不了时没有。
   */
  url?: string;
  blockedReason?: 'ui-paused';
  /**
   * 这个包的组件文件读不了(太大、缺失、越出包目录、是链接):包里每一项都带同一条,
   * 窗口据此只拒绝这个包的激活,其余包照常加载。
   */
  failure?: Readonly<{ code: string; message: string }>;
}>;

/** 浮层窗口向 main 取自己要渲染的组件与当前状态(按窗口令牌认领)。 */
export type ExtensionOverlaySessionDto = Readonly<{
  packageId: string;
  overlayId: string;
  source: string;
  state: unknown;
}>;

/**
 * `host.linkClaims.lookup` 的命中结果:`method` 可直喂 `host.capability.invoke`;
 * `browser` 是该认领声明的页面状态(浏览器连接器的采集计划用它)。
 */
export type ExtensionLinkClaimMatchDto = Readonly<{
  packageId: string;
  claimId: string;
  namespace: string;
  method: string;
  browser?: Readonly<{ state: readonly string[] }>;
}>;

export type ExtensionPackageTrustDto = Readonly<{
  signatureStatus: 'signed' | 'unsigned';
  sourceType: 'local-directory';
  /** Main-computed SHA-256 identity; never a local path. */
  sourceFingerprint: string;
  /** SHA-256 Ed25519 public-key fingerprint, or null for unsigned packages. */
  developerFingerprint: string | null;
}>;

export type InstalledExtensionPackageDto = Readonly<{
  packageId: string;
  packageVersion: string;
  enabled: boolean;
  installedAt: number;
  updatedAt: number;
  trust: ExtensionPackageTrustDto;
}>;

/**
 * 插件中心「加载开发目录」(D647 期 4):开发目录覆盖同 id 的已安装包,只在内存里。
 * 停止、应用退出或工作区关闭即回到已安装包。
 */
export type ExtensionDevelopmentDirectoryDto = Readonly<{
  packageId: string;
  /** 用户选的目录(插件源码根或它的 `dist/package`)。 */
  directory: string;
  /** 正在运行的开发构建;null 表示还没有成功加载过,运行的仍是已安装版本。 */
  packageVersion: string | null;
  installedVersion: string;
  loadedAt: number | null;
  /** 最近一次加载失败的原因;上一次成功加载的开发构建继续运行。 */
  error: Readonly<{ code: string; message: string; at: number }> | null;
}>;

/** 包级依赖(`dependencies[]`)此刻满不满足:缺包、停用、版本或方法对不上。 */
export type ExtensionDependencyStatus = ThirdPartyDependencyDeclaration &
  Readonly<{
    available: boolean;
    reason: 'missing' | 'disabled' | 'version' | 'method' | null;
    missingMethods: readonly string[];
  }>;

/**
 * `extensions.list` 的一行:装在工作区的插件与应用自带的包在同一张列表里(期 17e),
 * 插件中心、插件主页与其余读包的地方读这一份。`origin` 区分两种来源——自带包随应用
 * 发版,没有安装记录、信任与后台进程,这些字段它就不报。
 */
export type ExtensionPackageManagementDto =
  | InstalledExtensionPackageManagementDto
  | BuiltinExtensionPackageManagementDto;

/** 列表里装在工作区的那些(有安装记录、信任、贡献与后台进程的)。 */
export function isInstalledExtensionPackage(
  item: ExtensionPackageManagementDto
): item is InstalledExtensionPackageManagementDto {
  return item.origin === 'installed';
}

/**
 * 应用自带的包(清单在 `shared/builtin-packages.ts`):小组件与页面正文是应用代码,
 * 由渲染端的自带包目录登记;这里只有清单身份、页面、主页概览与启停。
 */
export type BuiltinExtensionPackageManagementDto = Readonly<{
  origin: 'builtin';
  packageId: string;
  packageVersion: string;
  enabled: boolean;
  /** 自带包数据里写了 `canDisable` 的才能停用(`builtin-packages.ts`),其余随应用常开。 */
  toggleable: boolean;
  title: Readonly<{ zh: string; en: string }>;
  description: Readonly<{ zh: string; en: string }>;
  icon: string;
  pages?: readonly ThirdPartyPageDeclaration[];
  home: import('@xuwo/contracts').ExtensionHomeOverview;
  status: 'ready';
}>;

export type InstalledExtensionPackageManagementDto =
  InstalledExtensionPackageDto &
    Readonly<{
      origin: 'installed';
      /** 清单的包级身份(16a);清单读不出来(`restore-failed`)时缺省。 */
      title?: Readonly<{ zh: string; en: string }>;
      description?: Readonly<{ zh: string; en: string }>;
      icon?: string;
      nativeTrust: 'not-required' | 'trusted' | 'requires-user-acceptance';
      hasCompiledWidgets: boolean;
      /** 应用自带(`bundled.json`);随应用发版,不能单独发布到社区。 */
      bundled?: true;
      widgets?: readonly Pick<
        ThirdPartyBundleWidgetDeclaration,
        'id' | 'title' | 'description' | 'size' | 'placement'
      >[];
      dependencies?: readonly ExtensionDependencyStatus[];
      pages?: readonly ThirdPartyPageDeclaration[];
      /**
       * 插件主页的清单概览(期 16e,`extensionHomeOverview`):渲染端按它生成主页正文。
       * 清单读不出来(`restore-failed`)时缺省,已有的主页保持原样。
       */
      home?: import('@xuwo/contracts').ExtensionHomeOverview;
      /** 本包声明的实体类型(`entityTypes[]`);样式、解析、阅读都从这里读。 */
      entityTypes?: readonly ExtensionEntityTypeContributionDto[];
      /**
       * 本包声明的出厂文档模板的 key(`documentTemplates[]` 与旧写法
       * `bundle.templates[]`):渲染端不读正文就能核对每篇模板的对齐戳(D753)。
       */
      documentTemplateKeys?: readonly string[];
      /** Installed skill declarations; body remains in the ordinary document library. */
      skills?: readonly Pick<ThirdPartyAgentSkillDeclaration, 'slug'>[];
      /** 清单 `commands[]`(D800):命令面板、页头菜单、选中文字工具栏与 AI 回答下的入口。 */
      commands?: readonly ExtensionCommandContributionDto[];
      /** 清单 `settings[]`(D800):「扩展设置」弹窗按它画;值经 `plugins.settings` 读写。 */
      settings?: readonly ThirdPartySettingDeclaration[];
      /** 呈现偏好:暂停本包的界面(小组件不挂载),不影响后台进程。 */
      uiPaused: boolean;
      /** 本包的后台进程(清单 `utility`);没有就不起进程。 */
      utility?: Readonly<{
        runtimeState: ExtensionRuntimeStateDto['state'];
        /** 崩溃太密、宿主不再自动重启时的诊断(见 `ExtensionRuntimeStateDto.failure`)。 */
        runtimeFailure?: ExtensionRuntimeStateDto['failure'];
      }>;
      /** `activitySources[]`(R3 6a):渲染端按它派生经历历史搜索提供者,不为具体来源写死字面量。 */
      activitySources: readonly ExtensionActivitySourceContributionDto[];
      status: 'ready' | 'restore-failed';
      error?: Readonly<{
        code: ExtensionHostPublicErrorCode;
        message: string;
      }>;
      development?: ExtensionDevelopmentDirectoryDto;
    }>;

export type ExtensionDoctorDto = Readonly<{
  ok: boolean;
  packageCatalog: 'healthy' | 'degraded';
  installedPackageCount: number;
  enabledPackageCount: number;
  restoreFailureCount: number;
  /** 包目录里没被安装记录引用的残留目录数(启动时清掉,见 package-service)。 */
  unreferencedPackageDirectories: number;
}>;

export type ExtensionFolderActionReceipt =
  | Readonly<{ status: 'cancelled' }>
  | Readonly<{
      status: 'completed';
      packageId: string;
      trust: ExtensionPackageTrustDto;
    }>;

/** 插件中心「检查插件文件夹…」的回执:所选目录里的包身份与来源证据,没装。 */
export type ExtensionPackageInspectionReceipt =
  | Readonly<{ status: 'cancelled' }>
  | Readonly<{
      status: 'completed';
      /** 用户自己选的目录;对话框里「安装」原样交回 `install`。 */
      directory: string;
      packageId: string;
      packageVersion: string;
      title: Readonly<{ zh: string; en: string }>;
      /** 含本机代码:安装时要用户允许它在这台电脑上运行(已允许过的来源不再问)。 */
      nativeCode: boolean;
      trust: ExtensionPackageTrustDto;
    }>;

export type ExtensionHostMetadataDto = Readonly<{
  apiVersion: typeof THIRD_PARTY_EXTENSION_RUNTIME_API_VERSION;
  platform: 'desktop';
}>;

/**
 * utility 进程发给宿主的一次调用。方法名、参数形状与校验规则只有一份 ——
 * `@xuwo/contracts` 的 `HOST_METHODS`(D647);这里只加请求信封。
 */
export type ExtensionExecutorRequest = {
  [K in HostMethodName]: Readonly<
    {
      id: string;
      workspaceId: string;
      /** 发请求的插件进程;进程身份就是包 id(16a)。 */
      runtimeId: string;
      method: K;
    } & ExtensionExecutorParams<K>
  >;
}[HostMethodName];

/** 无参数的方法没有 `params` 字段;参数整体可选的方法 `params` 可选。 */
type ExtensionExecutorParams<K extends HostMethodName> = [
  HostMethodCalls[K][0],
] extends [undefined]
  ? { params?: undefined }
  : undefined extends HostMethodCalls[K][0]
    ? { params?: HostMethodCalls[K][0] }
    : { params: HostMethodCalls[K][0] };

/** main 从本运行时仍在途的 provider 调用解析,永不从插件请求 JSON 接收。 */
export type ExtensionInvocationOrigin = Readonly<{
  workspaceId: string;
  actor: ActorRef;
  runId?: string;
}>;

export type ExtensionRuntimeRpcRequest = Readonly<{
  type: 'request';
  token?: string;
  id: string;
  method: ExtensionExecutorRequest['method'];
  /** utility 宿主自动附的 provider 调用号,main 只认本端口的在途请求。 */
  invocationId?: string;
  params?: unknown;
}>;

export type ExtensionRuntimeRpcResponse =
  | Readonly<{
      type: 'response';
      id: string;
      ok: true;
      value: unknown;
    }>
  | Readonly<{
      type: 'response';
      id: string;
      ok: false;
      error: 'invalid-request' | 'not-authorized' | 'runtime-failed';
      /**
       * 宿主这次调用失败的稳定码、脱敏说明与可否重试(如调了不存在的能力方法得到
       * `CAPABILITY_NOT_FOUND`)。utility 运行时把它挂到抛给插件的 Error 上
       * (`code` / `retryable`),插件原样抛出时码一路回到调用方。
       */
      failure?: ExtensionProviderFailure;
    }>;

/**
 * 宿主能向插件 provider 发起的方法。**一份清单两处用**:类型在主进程一侧,
 * utility 进程用同一个数组当运行时白名单 —— 名单外的消息按协议违例处理并结束
 * 进程,所以两边分开维护过一次就会把插件进程打崩(2026-09-22 `run.cancel`)。
 * 名单与每个钩子的参数 / 结果合同都在 `@xuwo/contracts`(`PROVIDER_HOOK_NAMES` /
 * `PROVIDER_HOOKS`,18a)。
 */
export const EXTENSION_PROVIDER_RPC_METHODS = PROVIDER_HOOK_NAMES;

export type ExtensionProviderRpcMethod = ProviderHookName;

/** 插件不处理取消时的回答:宿主据此让开,按「没有执行方」收尾这条运行。 */
export { EXTENSION_RUN_CANCEL_UNSUPPORTED } from '@xuwo/contracts';

export type ExtensionProviderRpcRequest = Readonly<{
  type: 'provider-request';
  token: string;
  id: string;
  workspaceId: string;
  method: ExtensionProviderRpcMethod;
  params: unknown;
}>;

export type ExtensionProviderRpcCancel = Readonly<{
  type: 'provider-cancel';
  token: string;
  id: string;
}>;

export type ExtensionProviderRpcResponse =
  | Readonly<{
      type: 'provider-response';
      id: string;
      ok: true;
      value: unknown;
    }>
  | Readonly<{
      type: 'provider-response';
      id: string;
      ok: false;
      error: 'invalid-request' | 'runtime-failed';
      /** 插件主动抛出的业务错误:稳定码 + 可展示说明,宿主原样转成能力错误。 */
      failure?: ExtensionProviderFailure;
    }>;

export type ExtensionProviderFailure = Readonly<{
  code: string;
  message: string;
  retryable: boolean;
}>;

/** 插件错误码与宿主内建错误码同形:大写下划线。 */
export const EXTENSION_PROVIDER_FAILURE_CODE = /^[A-Z][A-Z0-9_]{2,63}$/u;

export type ExtensionRuntimeBootstrap = Readonly<{
  type: 'xuwo-extension-bootstrap';
  apiVersion: typeof THIRD_PARTY_EXTENSION_RUNTIME_API_VERSION;
  /** 进程身份,即包 id(16a)。 */
  runtimeId: string;
  entrypoint: string;
  token: string;
  /** 本包的应用级数据目录,宿主已建好;插件自管其中的文件与迁移。 */
  dataDir?: string;
  /** 本进程所属工作区;宿主每个工作区各起一个插件进程。 */
  workspaceId?: string;
}>;

/** 一个包的后台进程(进程身份就是包 id)。 */
export type ExtensionRuntimeStateDto = Readonly<{
  packageId: string;
  packageVersion: string;
  state: 'stopped' | 'starting' | 'running' | 'crashed';
  /**
   * 常驻进程在 `windowMs` 内崩了 `crashes` 次,宿主不再自动重启;用户手动启动
   * (插件中心的启动 / 修复)后清掉。只在 `state: 'crashed'` 时出现。
   */
  failure?: Readonly<{
    code: 'EXTENSION_RUNTIME_CRASH_LOOP';
    crashes: number;
    windowMs: number;
  }>;
}>;

export type ExtensionHostPublicErrorCode =
  | 'invalid-request'
  | 'not-found'
  | 'not-authorized'
  | 'conflict'
  | 'runtime-failed';

export type ExtensionHostResult<T> =
  | Readonly<{ ok: true; value: T }>
  | Readonly<{
      ok: false;
      error: Readonly<{
        code: ExtensionHostPublicErrorCode;
        message: string;
      }>;
    }>;

export type ExtensionLocalSourceStatusDto = Readonly<{
  localDirectoryInstall: 'available';
  localIndex: 'available';
  remoteCatalog: 'disabled-no-service';
  certificateAuthority: 'disabled-no-service';
}>;

export type ExtensionLocalIndexDto = Readonly<{
  source: ExtensionLocalSourceStatusDto;
  packages: readonly InstalledExtensionPackageDto[];
}>;

/**
 * 清单声明的类型只有一份,在 `@xuwo/contracts` 的 `extension-manifest.ts`(D647);
 * 这里的旧名字是别名,消费者逐步改用契约名。
 */
export { EXTENSION_MANIFEST_API_VERSION as THIRD_PARTY_EXTENSION_RUNTIME_API_VERSION } from '@xuwo/contracts';
export type ThirdPartyAgentSkillDeclaration =
  import('@xuwo/contracts').ExtensionAgentSkillDeclaration;
export type ThirdPartyActivitySourceDeclaration =
  import('@xuwo/contracts').ExtensionActivitySourceDeclaration;
export type ThirdPartyCapabilityNamespaceDeclaration =
  import('@xuwo/contracts').ExtensionCapabilityNamespaceDeclaration;
export type ThirdPartyDependencyDeclaration =
  import('@xuwo/contracts').ExtensionDependencyDeclaration;
export type ThirdPartyBundleWidgetDeclaration =
  import('@xuwo/contracts').ExtensionBundleWidgetDeclaration;
export type ThirdPartyBundleDeclaration =
  import('@xuwo/contracts').ExtensionBundleDeclaration;
export type ThirdPartyRuntimePackageDescriptor =
  import('@xuwo/contracts').ExtensionManifest;
export type ThirdPartyOverlayDeclaration =
  import('@xuwo/contracts').ExtensionOverlayDeclaration;
export type ExtensionCommandSurface =
  import('@xuwo/contracts').ExtensionCommandSurface;
export type ThirdPartyCommandDeclaration =
  import('@xuwo/contracts').ExtensionCommandManifestDeclaration;
/** 一条命令连同「执行前要不要确认」(由被调方法的声明推出,D800 §3.7)。 */
export type ExtensionCommandContributionDto = ThirdPartyCommandDeclaration &
  Readonly<{ confirm: boolean }>;
export type ThirdPartySettingDeclaration =
  import('@xuwo/contracts').ExtensionSettingManifestDeclaration;
export type ThirdPartyLinkClaimDeclaration =
  import('@xuwo/contracts').ExtensionLinkClaimDeclaration;
export type ThirdPartyCaptureTargetDeclaration =
  import('@xuwo/contracts').ExtensionCaptureTargetDeclaration;
export type ThirdPartyPageDeclaration =
  import('@xuwo/contracts').ExtensionPageDeclaration;
export type ThirdPartySystemTaskDeclaration =
  import('@xuwo/contracts').ExtensionSystemTaskDeclaration;

/** 原生运行风险确认之后宿主要做的事;对话框按它写确认按钮(19h)。 */
export type SourceTrustAction = 'enable' | 'update' | 'load';

/** 一条在应用窗口里等用户回答的原生运行风险确认(19h,`source-trust-prompt.ts`)。 */
export type SourceTrustPrompt = Readonly<{
  id: string;
  workspaceId: string;
  packageId: string;
  /** 清单的包名;没有(包还读不出来)时界面显示 `packageId`。 */
  title: Readonly<{ zh: string; en: string }> | null;
  packageVersion: string | null;
  /**
   * 这个包挂进 Agent 运行时的地方(清单 `agentHooks[]` 与 `toolCards[]` 生成,D798);
   * 没有就缺省。信任仍是对整个来源的一次确认,不逐项授权。
   */
  agent?: import('@xuwo/contracts').ExtensionAgentSurface;
  /** 清单 `commands[]` 的标题、位置与默认键位(D800);没有命令是空数组。 */
  commands: readonly Readonly<{
    title: Readonly<{ zh: string; en: string }>;
    surfaces: readonly ExtensionCommandSurface[];
    keybinding?: string;
  }>[];
  action: SourceTrustAction;
  requestedAt: number;
}>;

/** `extensions:onSourceTrustPrompt`:新的待确认,或某条已落定(窗口据此关掉对话框)。 */
export type SourceTrustPromptEvent =
  | Readonly<{
      type: 'opened';
      workspaceId: string;
      prompt: SourceTrustPrompt;
    }>
  | Readonly<{
      type: 'settled';
      workspaceId: string;
      id: string;
      outcome: 'accepted' | 'declined' | 'timeout' | 'withdrawn';
    }>;
